import React, { useState, useEffect } from 'react';
import api from '../api';

const AdminFeeManager = () => {
    const [fees, setFees] = useState([]);
    const [students, setStudents] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showPartialPaymentModal, setShowPartialPaymentModal] = useState(false);
    const [selectedFee, setSelectedFee] = useState(null);
    const [partialPaymentData, setPartialPaymentData] = useState({
        paymentAmount: '',
        note: '',
    });
    const [formData, setFormData] = useState({
        student: '',
        class: '',
        amount: '',
        dueDate: '',
        description: 'Học phí',
        note: '',
    });

    useEffect(() => {
        fetchFees();
        fetchStudents();
        fetchClasses();
    }, []);

    const fetchFees = async () => {
        try {
            const res = await api.get('/fees/all');
            // Handle both old format (array) and new paginated format
            const feesData = res.data.success && res.data.fees
                ? res.data.fees.data
                : res.data;
            setFees(Array.isArray(feesData) ? feesData : []);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching fees:', err);
            setFees([]);
            setLoading(false);
        }
    };

    const fetchStudents = async () => {
        try {
            const res = await api.get('/users/students');
            // Handle both old format (array) and new paginated format
            const studentsData = res.data.success && res.data.students
                ? res.data.students.data
                : res.data;
            setStudents(Array.isArray(studentsData) ? studentsData : []);
        } catch (err) {
            console.error('Error fetching students:', err);
            setStudents([]);
        }
    };

    const fetchClasses = async () => {
        try {
            const res = await api.get('/classes');
            // Handle both old format (array) and new paginated format
            const classesData = res.data.success && res.data.classes
                ? res.data.classes.data
                : res.data;
            setClasses(Array.isArray(classesData) ? classesData : []);
        } catch (err) {
            console.error('Error fetching classes:', err);
            setClasses([]);
        }
    };

    const handleCreateFee = async (e) => {
        e.preventDefault();
        try {
            await api.post('/fees', formData);
            setFormData({
                student: '',
                class: '',
                amount: '',
                dueDate: '',
                description: 'Học phí',
                note: '',
            });
            setShowCreateForm(false);
            fetchFees();
            showNotification('Đã tạo khoản phí thành công!', 'success');
        } catch (err) {
            console.error('Error creating fee:', err);
            showNotification('Lỗi khi tạo khoản phí: ' + (err.response?.data?.msg || 'Unknown error'), 'error');
        }
    };

    const handleMarkPaid = async (feeId) => {
        if (window.confirm('Xác nhận đã nhận thanh toán đầy đủ cho khoản phí này?')) {
            try {
                await api.put(`/fees/${feeId}/mark-paid`);
                fetchFees();
                showNotification('Đã đánh dấu là đã thanh toán!', 'success');
            } catch (err) {
                console.error('Error marking fee as paid:', err);
                showNotification('Lỗi khi cập nhật trạng thái', 'error');
            }
        }
    };

    const handleOpenPartialPayment = (fee) => {
        setSelectedFee(fee);
        setPartialPaymentData({
            paymentAmount: '',
            note: '',
        });
        setShowPartialPaymentModal(true);
    };

    const handlePartialPayment = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/fees/${selectedFee._id}/partial-payment`, partialPaymentData);
            setShowPartialPaymentModal(false);
            setSelectedFee(null);
            setPartialPaymentData({ paymentAmount: '', note: '' });
            fetchFees();
            showNotification('Đã ghi nhận thanh toán một phần!', 'success');
        } catch (err) {
            console.error('Error recording partial payment:', err);
            showNotification(
                err.response?.data?.msg || 'Lỗi khi ghi nhận thanh toán',
                'error'
            );
        }
    };

    const handleDeleteFee = async (feeId) => {
        if (window.confirm('Bạn có chắc muốn xóa khoản phí này?')) {
            try {
                await api.delete(`/fees/${feeId}`);
                fetchFees();
                showNotification('Đã xóa khoản phí!', 'success');
            } catch (err) {
                console.error('Error deleting fee:', err);
                showNotification('Lỗi khi xóa khoản phí', 'error');
            }
        }
    };

    const showNotification = (message, type) => {
        const notification = document.createElement('div');
        notification.className = `fixed top-4 right-4 z-50 rounded-xl px-6 py-4 shadow-lg ${
            type === 'success' ? 'bg-green-600' : 'bg-red-600'
        } text-white`;
        notification.innerHTML = `
            <div class="flex items-center gap-3">
                <i class="fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'} text-xl"></i>
                <span class="font-medium">${message}</span>
            </div>
        `;
        document.body.appendChild(notification);
        setTimeout(() => notification.remove(), 5000);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'paid':
                return 'bg-green-100 text-green-800';
            case 'partial':
                return 'bg-blue-100 text-blue-800';
            case 'overdue':
                return 'bg-red-100 text-red-800';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-slate-100 text-slate-800';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'paid':
                return 'Đã thanh toán';
            case 'partial':
                return 'Thanh toán một phần';
            case 'overdue':
                return 'Quá hạn';
            case 'pending':
                return 'Chưa thanh toán';
            default:
                return status;
        }
    };

    const totalRevenue = fees
        .filter((fee) => fee.status === 'paid')
        .reduce((sum, fee) => sum + fee.amount, 0);

    const totalPending = fees
        .filter((fee) => fee.status !== 'paid')
        .reduce((sum, fee) => sum + fee.amount, 0);

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600"></div>
                <p className="mt-2 text-sm text-slate-600">Đang tải...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-slate-900">Quản lý học phí</h2>
                <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700"
                >
                    <i className="fas fa-plus" aria-hidden></i>
                    Tạo khoản phí mới
                </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-2xl bg-gradient-to-br from-green-500 to-green-600 p-6 text-white">
                    <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                            <i className="fas fa-money-bill-wave text-xl" aria-hidden></i>
                        </div>
                        <div>
                            <p className="text-sm opacity-80">Tổng đã thu</p>
                            <p className="text-2xl font-bold">{formatCurrency(totalRevenue)}</p>
                        </div>
                    </div>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-yellow-500 to-yellow-600 p-6 text-white">
                    <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                            <i className="fas fa-clock text-xl" aria-hidden></i>
                        </div>
                        <div>
                            <p className="text-sm opacity-80">Chưa thanh toán</p>
                            <p className="text-2xl font-bold">{formatCurrency(totalPending)}</p>
                        </div>
                    </div>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white">
                    <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
                            <i className="fas fa-receipt text-xl" aria-hidden></i>
                        </div>
                        <div>
                            <p className="text-sm opacity-80">Tổng số khoản</p>
                            <p className="text-2xl font-bold">{fees.length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Create Form */}
            {showCreateForm && (
                <div className="rounded-2xl bg-slate-50 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Tạo khoản phí mới</h3>
                    <form onSubmit={handleCreateFee} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Sinh viên *
                                </label>
                                <select
                                    value={formData.student}
                                    onChange={(e) => setFormData({ ...formData, student: e.target.value })}
                                    required
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                >
                                    <option value="">Chọn sinh viên</option>
                                    {students.map((student) => (
                                        <option key={student._id} value={student._id}>
                                            {student.name} - {student.email}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Lớp học *
                                </label>
                                <select
                                    value={formData.class}
                                    onChange={(e) => setFormData({ ...formData, class: e.target.value })}
                                    required
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                >
                                    <option value="">Chọn lớp học</option>
                                    {classes.map((cls) => (
                                        <option key={cls._id} value={cls._id}>
                                            {cls.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Số tiền (VNĐ) *
                                </label>
                                <input
                                    type="number"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    required
                                    min="0"
                                    step="1000"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    placeholder="Nhập số tiền"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Hạn thanh toán *
                                </label>
                                <input
                                    type="date"
                                    value={formData.dueDate}
                                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                                    required
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Mô tả
                                </label>
                                <input
                                    type="text"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    placeholder="Học phí"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Ghi chú
                                </label>
                                <input
                                    type="text"
                                    value={formData.note}
                                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    placeholder="Ghi chú thêm..."
                                />
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-indigo-700"
                            >
                                <i className="fas fa-save" aria-hidden></i>
                                Tạo khoản phí
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCreateForm(false);
                                    setFormData({
                                        student: '',
                                        class: '',
                                        amount: '',
                                        dueDate: '',
                                        description: 'Học phí',
                                        note: '',
                                    });
                                }}
                                className="rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow transition hover:bg-slate-50"
                            >
                                Hủy
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Fee List */}
            <div className="overflow-hidden rounded-2xl border border-slate-200">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                                Sinh viên
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                                Lớp học
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                                Mô tả
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                                Số tiền
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                                Hạn
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                                Trạng thái
                            </th>
                            <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                                Thao tác
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                        {fees.map((fee) => (
                            <tr key={fee._id} className="hover:bg-slate-50">
                                <td className="px-6 py-4 text-sm font-medium text-slate-900">
                                    {fee.student?.name || 'N/A'}
                                    <br />
                                    <span className="text-xs text-slate-500">{fee.student?.email || ''}</span>
                                </td>
                                <td className="px-6 py-4 text-sm text-slate-600">
                                    {fee.class?.name || 'N/A'}
                                </td>
                                <td className="px-6 py-4 text-sm text-slate-600">
                                    {fee.description}
                                    {fee.note && (
                                        <div className="text-xs text-slate-500 italic mt-1">
                                            {fee.note}
                                        </div>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-sm">
                                    <div className="font-semibold text-indigo-600">
                                        {formatCurrency(fee.amount)}
                                    </div>
                                    {fee.paidAmount > 0 && fee.status !== 'paid' && (
                                        <div className="text-xs text-slate-600 mt-1">
                                            Đã trả: {formatCurrency(fee.paidAmount)}
                                            <br />
                                            Còn lại: <span className="font-semibold text-orange-600">
                                                {formatCurrency(fee.remainingAmount)}
                                            </span>
                                        </div>
                                    )}
                                    {fee.status === 'paid' && fee.paidAmount > 0 && (
                                        <div className="text-xs text-green-600 mt-1">
                                            Đã thanh toán: {formatCurrency(fee.paidAmount)}
                                        </div>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-sm text-slate-600">
                                    {formatDate(fee.dueDate)}
                                    {fee.paidDate && (
                                        <div className="text-xs text-green-600 mt-1">
                                            Đã trả: {formatDate(fee.paidDate)}
                                        </div>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-sm">
                                    <span
                                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(
                                            fee.status
                                        )}`}
                                    >
                                        {getStatusText(fee.status)}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-center">
                                    <div className="flex items-center justify-center gap-2 flex-wrap">
                                        {fee.status !== 'paid' && (
                                            <>
                                                <button
                                                    onClick={() => handleOpenPartialPayment(fee)}
                                                    className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-blue-700"
                                                    title="Thanh toán một phần"
                                                >
                                                    <i className="fas fa-coins" aria-hidden></i>
                                                    Trả 1 phần
                                                </button>
                                                <button
                                                    onClick={() => handleMarkPaid(fee._id)}
                                                    className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-green-700"
                                                    title="Đánh dấu đã thanh toán đầy đủ"
                                                >
                                                    <i className="fas fa-check" aria-hidden></i>
                                                    Trả đủ
                                                </button>
                                            </>
                                        )}
                                        <button
                                            onClick={() => handleDeleteFee(fee._id)}
                                            className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-red-700"
                                            title="Xóa khoản phí"
                                        >
                                            <i className="fas fa-trash" aria-hidden></i>
                                            Xóa
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {fees.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-6 py-8 text-center text-slate-500">
                                    Chưa có khoản phí nào
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Partial Payment Modal */}
            {showPartialPaymentModal && selectedFee && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowPartialPaymentModal(false)}>
                    <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Thanh toán một phần</h3>

                        <div className="mb-4 p-4 bg-slate-50 rounded-xl">
                            <div className="text-sm text-slate-600 mb-2">
                                <strong>Sinh viên:</strong> {selectedFee.student?.name}
                            </div>
                            <div className="text-sm text-slate-600 mb-2">
                                <strong>Tổng học phí:</strong> {formatCurrency(selectedFee.amount)}
                            </div>
                            <div className="text-sm text-slate-600 mb-2">
                                <strong>Đã thanh toán:</strong> {formatCurrency(selectedFee.paidAmount || 0)}
                            </div>
                            <div className="text-sm font-semibold text-orange-600">
                                <strong>Còn lại:</strong> {formatCurrency(selectedFee.remainingAmount || selectedFee.amount)}
                            </div>
                        </div>

                        <form onSubmit={handlePartialPayment} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Số tiền thanh toán (VNĐ) *
                                </label>
                                <input
                                    type="number"
                                    value={partialPaymentData.paymentAmount}
                                    onChange={(e) => setPartialPaymentData({ ...partialPaymentData, paymentAmount: e.target.value })}
                                    required
                                    min="1"
                                    max={selectedFee.remainingAmount || selectedFee.amount}
                                    step="1"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    placeholder="Nhập số tiền"
                                />
                                <p className="mt-1 text-xs text-slate-500">
                                    Tối đa: {formatCurrency(selectedFee.remainingAmount || selectedFee.amount)}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Ghi chú
                                </label>
                                <textarea
                                    value={partialPaymentData.note}
                                    onChange={(e) => setPartialPaymentData({ ...partialPaymentData, note: e.target.value })}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    placeholder="Ghi chú về khoản thanh toán..."
                                    rows="3"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-indigo-700"
                                >
                                    <i className="fas fa-check" aria-hidden></i>
                                    Xác nhận
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowPartialPaymentModal(false)}
                                    className="flex-1 rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow transition hover:bg-slate-50"
                                >
                                    Hủy
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminFeeManager;
